import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApi } from '../hooks/useApi';
import { apiService, getApiUrl, getCurrentLocale } from '../services/api';
import { ArrowRight } from './icons';
import { SlotMachineButton } from './SlotMachineButton';
import { getBestImageUrl } from '../utils/imageUrl';

// Import local SVG assets for fallbacks
// Note: Using require() to avoid TypeScript module resolution issues
const consultationIcon = '/src/assets/images/consultation.svg';
const treatmentHairIcon = '/src/assets/images/treatment-hair.svg';
const teamPhotoIcon = '/src/assets/images/team-photo.svg';

// Utility function for className merging
function cn(...inputs: (string | undefined | null | false)[]): string {
  return inputs.filter(Boolean).join(' ');
}

// Utility function to format step numbers with leading zeros
function formatStepNumber(number: string | number): string {
  const num = typeof number === 'string' ? parseInt(number, 10) : number;
  return num.toString().padStart(2, '0');
}

const useIsMobile = (query: string = '(max-width: 768px)') => {
  const isBrowser = typeof window !== 'undefined';
  
  const [isMobile, setIsMobile] = useState(isBrowser ? window.matchMedia(query).matches : false);

  useEffect(() => {
    if (!isBrowser) return;
    
    const mediaQuery = window.matchMedia(query);
    const handleChange = () => setIsMobile(mediaQuery.matches);

    mediaQuery.addEventListener('change', handleChange);
    handleChange();

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query, isBrowser]);

  return isMobile;
};

interface Step {
  id?: number;
  number: string;
  title: string;
  description: string;
  image?: string | {
    id?: number;
    url?: string;
    alternativeText?: string;
    formats?: {
      large?: { url: string; width: number; height: number; };
      medium?: { url: string; width: number; height: number; };
      small?: { url: string; width: number; height: number; };
      thumbnail?: { url: string; width: number; height: number; };
    };
    width?: number;
    height?: number;
  } | {
    data: {
      attributes: {
        url: string;
        alternativeText?: string;
      };
    };
  };
}

interface CTAButton {
  text: string;
  url: string;
  variant?: 'primary' | 'secondary' | 'tertiary';
}

interface ApproachSectionData {
  id?: number;
  documentId?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  steps?: Step[];
  ctaMessage?: string;
  ctaButtons?: CTAButton[] | {
    primary?: CTAButton;
    secondary?: CTAButton;
  };
  backgroundImage?: any;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

function ApproachSection(): React.JSX.Element {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [headerHeight, setHeaderHeight] = useState<number>(80); // Default fallback
  const [manualData, setManualData] = useState<any>(null);
  const locale = getCurrentLocale();
  const { data, loading, error } = useApi(apiService.getApproachSection, [locale]);
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Fallback data fetch if useApi doesn't return data
  useEffect(() => {
    if (!data && !loading) {
      // Make a direct API call as fallback
      const fetchDirectly = async () => {
        try {
          // Use the same API base resolution as axios service
          const apiUrl = getApiUrl();
          const response = await fetch(`${apiUrl}/behandelingen-approach?populate=image&populate=steps&populate=steps.image`, {
            // Align timeout behavior: implement manual timeout wrapper for fetch
            signal: (() => {
              const controller = new AbortController();
              setTimeout(() => controller.abort(), 20000);
              return controller.signal;
            })()
          });
          const rawData = await response.json();
          
          console.log('🔄 ApproachSection - Manual fetch response:', rawData);
          
          // Set manual data if available (Strapi v4 flat structure)
          if (rawData.data && rawData.data.steps) {
            console.log('🔄 ApproachSection - Setting manual data with steps:', rawData.data.steps.length);
            setManualData(rawData.data);
          }
        } catch (error) {
          console.error('Direct API call failed:', error);
        }
      };
      
      fetchDirectly();
    }
  }, [data, loading]);

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const titleVariants = {
    hidden: { y: -20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  // Measure header height dynamically
  useEffect(() => {
    if (isMobile) return;

    const measureHeaderHeight = () => {
      const headerSelectors = ['header', '.header', '.sticky-header', '[data-sticky="true"]', 'nav'];
      
      for (const selector of headerSelectors) {
        const headerElement = document.querySelector(selector);
        if (headerElement) {
          const height = headerElement.getBoundingClientRect().height;
          setHeaderHeight(height + 20); // Add some padding
          break;
        }
      }
    };

    measureHeaderHeight();
    window.addEventListener('resize', measureHeaderHeight);
    
    return () => window.removeEventListener('resize', measureHeaderHeight);
  }, [isMobile]);

  // Track scroll and update active step based on phase visibility
  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      const phaseElements = document.querySelectorAll('.phase-text');
      const ctaElement = document.querySelector('.phase-cta-text');
      const windowCenter = window.innerHeight / 2;
      
      let closestIndex = 0;
      let closestDistance = Infinity;
      
      phaseElements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const elementCenter = rect.top + rect.height / 2;
        const distance = Math.abs(elementCenter - windowCenter);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      
      if (ctaElement) {
        const rect = ctaElement.getBoundingClientRect();
        const elementCenter = rect.top + rect.height / 2;
        const distance = Math.abs(elementCenter - windowCenter);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = phaseElements.length;
        }
      }
      
      setActiveStep(closestIndex);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Default steps as fallback with local SVG icons
  const defaultSteps: Step[] = [
    {
      number: "01",
      title: "Eerste consultatie",
      description: "We beginnen met een uitgebreid consult om jouw specifieke situatie en wensen te begrijpen.",
      image: consultationIcon
    },
    {
      number: "02", 
      title: "Behandelplan op maat",
      description: "Op basis van de consultatie stellen we een persoonlijk behandelplan voor je samen.",
      image: treatmentHairIcon
    },
    {
      number: "03",
      title: "Professionele behandeling",
      description: "Onze ervaren specialisten voeren de behandeling uit met de nieuwste technieken.",
      image: teamPhotoIcon
    }
  ];

  // Use manual data if available, otherwise use data from API or fallback to default
  const dataToUse = manualData || data;
  
  // Debug logging for approach section data
  if (data && !loading && !error) {
    console.log('🎯 ApproachSection - ✅ Strapi data loaded:', !!data);
    console.log('🎯 ApproachSection - Data keys:', Object.keys(data || {}));
    console.log('🎯 ApproachSection - Title:', (data as any).title);
    console.log('🎯 ApproachSection - Steps count:', (data as any).steps?.length || 0);
    console.log('🎯 ApproachSection - Steps structure:', (data as any).steps);
    console.log('🎯 ApproachSection - Has image:', !!(data as any).image);
  }

  if (loading) {
    console.log('🎯 ApproachSection - Loading Strapi data...');
  }

  if (error) {
    console.log('🎯 ApproachSection - ❌ Error loading Strapi data:', error);
    console.log('🎯 ApproachSection - Falling back to manual fetch or defaults');
  }
  // Localized default titles
  const defaultTitle = locale === 'ar' ? 'النهج' : 'Aanpak';
  const title = (dataToUse as ApproachSectionData | null)?.title || defaultTitle;
  const subtitle = (dataToUse as ApproachSectionData | null)?.subtitle || '';
  const description = (dataToUse as ApproachSectionData | null)?.description || '';
  const steps = (dataToUse as ApproachSectionData | null)?.steps || defaultSteps;
  const ctaMessage = (dataToUse as ApproachSectionData | null)?.ctaMessage || 'Voor een persoonlijke plan van aanpak, neem contact met ons op. Ontdek hoe wij u verder kunnen helpen.';
  
  // Debug final extracted values
  console.log('🔍 ApproachSection - Final extracted values:');
  console.log('🔍 Title:', title);
  console.log('🔍 Subtitle:', subtitle);
  console.log('🔍 Description:', description);
  console.log('🔍 DataToUse:', dataToUse);
  console.log('🔍 DataToUse has image:', !!dataToUse?.image);
  console.log('🔍 DataToUse image details:', dataToUse?.image);
  // Handle ctaButtons - can be array (old format) or object with primary/secondary (new format)
  const rawCtaButtons = (dataToUse as ApproachSectionData | null)?.ctaButtons;
  let ctaButtons: CTAButton[] = [];
  
  if (Array.isArray(rawCtaButtons)) {
    // Old format: array of buttons
    ctaButtons = rawCtaButtons;
  } else if (rawCtaButtons && typeof rawCtaButtons === 'object') {
    // New format: object with primary/secondary
    const buttonObj = rawCtaButtons as { primary?: CTAButton; secondary?: CTAButton };
    if (buttonObj.primary) ctaButtons.push(buttonObj.primary);
    if (buttonObj.secondary) ctaButtons.push(buttonObj.secondary);
  }
  
  // Fallback if no buttons found
  if (ctaButtons.length === 0) {
    ctaButtons = [{ text: 'Lees meer over de behandeling', url: '#behandelingen', variant: 'tertiary' }];
  }



  // Helper function to get image URL for a step
  const getStepImageUrl = (step: Step): string => {
    console.log(`🔍 getStepImageUrl for "${step.title}":`, {
      hasDataToUse: !!dataToUse,
      hasStepImage: !!(step.image),
      stepImageType: typeof step.image,
      stepImageName: step.image && typeof step.image === 'object' && 'name' in step.image ? step.image.name : 'unknown'
    });
    
    // First priority: Check if step has its own image
    if (step.image && typeof step.image === 'object' && 'url' in step.image && step.image.url) {
      const imageUrl = getBestImageUrl(step.image as any);
      if (imageUrl) {
        console.log(`🖼️ Step "${step.title}" - ✅ Using step-specific image: ${imageUrl.split('/').pop()}`);
        return imageUrl;
      }
    }
    
    // If step has old format (data.attributes)
    if (step.image && typeof step.image === 'object' && 'data' in step.image && step.image.data?.attributes?.url) {
      const imageUrl = getBestImageUrl(step.image.data.attributes);
      if (imageUrl) {
        console.log(`🖼️ Step "${step.title}" - ✅ Using step-specific image (old format): ${imageUrl.split('/').pop()}`);
        return imageUrl;
      }
    }
    
    // Second priority: Use shared section image as fallback
    const sharedImage = dataToUse?.image;
    if (sharedImage) {
      const imageUrl = getBestImageUrl(sharedImage);
      if (imageUrl) {
        console.log(`🖼️ Step "${step.title}" - ⚠️ Fallback to shared section image: ${imageUrl.split('/').pop()}`);
        return imageUrl;
      }
    }
    
    // Third priority: If step has a string image (fallback format)
    if (typeof step.image === 'string') {
      console.log(`🖼️ Step "${step.title}" - ⚠️ Using string image fallback: ${step.image}`);
      return step.image;
    }
    
    // Fallback based on step number if no image available
    const stepNumber = parseInt(step.number.toString());
    console.log(`⚠️ Step "${step.title}" - No image available, using local fallback for step ${stepNumber}`);
    switch (stepNumber) {
      case 1:
        return consultationIcon;
      case 2:
        return treatmentHairIcon;
      case 3:
        return teamPhotoIcon;
      default:
        return consultationIcon;
    }
  };

  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const parallaxElements = document.querySelectorAll('.approach-phase');
      
      parallaxElements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const speed = 0.5;
        const yPos = -(scrolled * speed);
        const backgroundElement = element.querySelector('.phase-background');
        
        if (backgroundElement && 'style' in backgroundElement) {
          (backgroundElement as HTMLElement).style.transform = `translateY(${yPos}px)`;
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Show loading state
  if (loading) {
    return (
      <div className="approach-container" id="aanpak">
        <div className="approach-header">
          <div className="loading-placeholder">
            <div className="loading-title"></div>
          </div>
        </div>
        {defaultSteps.map((step, index) => (
          <div key={index} className="approach-phase">
            <div className="phase-content">
              <div className="phase-text">
                <div className="loading-placeholder">
                  <div className="loading-title"></div>
                  <div className="loading-text"></div>
                </div>
              </div>
              <div className="phase-image">
                <img 
                  src={getStepImageUrl(step)} 
                  alt={step.title}
                  className="phase-image-img"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isMobile) {
    return (
      <motion.section 
        className="approach-container" 
        id="aanpak"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        dir={locale === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="approach-header">
          <motion.h2 className="approach-main-title" variants={titleVariants}>{title}</motion.h2>
        </div>
        
        <div className="mobile-approach-layout">
          {steps.map((step, index) => (
            <div key={index} className={cn("mobile-approach-phase", index === steps.length - 1 && 'last-phase')}>
              <div className="mobile-phase-text">
                <span className="step-number">{formatStepNumber(step.number)}</span>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
              <div className="mobile-phase-image">
                <img 
                  src={getStepImageUrl(step)} 
                  alt={step.title}
                  className="phase-image-img"
                />
              </div>
            </div>
          ))}
          
          <div className="mobile-phase-cta">
            <p className="cta-message">{ctaMessage}</p>
            <div className="cta-buttons">
              {ctaButtons.map((button, index) => (
                <SlotMachineButton
                  key={index}
                  text={button.text}
                  icon={<ArrowRight />}
                  href={button.url}
                  className={`btn btn-${button.variant || 'primary'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section 
      ref={ref}
      className="approach-container" 
      id="aanpak" 
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
      style={{
        position: 'relative',
        width: '100%',
        minHeight: 'fit-content',
        overflow: 'visible'
      }}>
      <div className="approach-header">
        <motion.h2 className="approach-main-title" variants={titleVariants}>{title}</motion.h2>
      </div>
      
      <div className="approach-phase" style={{
        position: 'relative',
        minHeight: 'auto',
        height: 'auto',
        paddingBottom: '120px'
      }}>
        <div className="phase-content" style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '2rem',
          minHeight: 'auto'
        }}>
          <div className="phases-container" style={{
            flex: 1,
            position: 'relative',
            zIndex: 1,
            paddingRight: '2rem',
            minHeight: 'fit-content'
          }}>
            {steps.map((step, index) => (
              <motion.div 
                key={index} 
                className="phase-text" 
                style={{
                  marginBottom: '4rem',
                  paddingTop: '8rem',
                  paddingBottom: '4rem',
                  minHeight: '60vh',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
                animate={{ 
                  opacity: activeStep === index ? 1 : 0.3 
                }}
                transition={{ duration: 0.3 }}
              >
                <div className="step-number">{formatStepNumber(step.number)}</div>
                <h3 className="step-title-approach">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </motion.div>
            ))}
            
            <motion.div 
              className="phase-cta-text" 
              style={{
                marginTop: '2rem',
                paddingTop: '8rem',
                paddingBottom: '4rem',
                minHeight: '60vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
              animate={{ 
                opacity: activeStep === steps.length ? 1 : 0.3
              }}
              transition={{ duration: 0.3 }}
            >
              <p className="cta-message">{ctaMessage}</p>
              
              <div className="cta-buttons">
                {ctaButtons.map((button, index) => (
                  <SlotMachineButton
                    key={index}
                    text={button.text}
                    icon={<ArrowRight />}
                    href={button.url}
                    className={`btn btn-${button.variant || 'primary'}`}
                  />
                ))}
              </div>
            </motion.div>
          </div>

          {activeStep < steps.length && (
            <motion.div 
              className="phase-image" 
              style={{
                position: 'sticky',
                top: `${headerHeight + 20}px`,
                flex: '0 0 50%',
                height: `calc(100vh - ${headerHeight + 80}px)`,
                maxHeight: '600px',
                alignSelf: 'flex-start',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              <AnimatePresence>
                {activeStep < steps.length && (
                  <motion.img 
                    key={activeStep}
                    src={getStepImageUrl(steps[activeStep])} 
                    alt={steps[activeStep]?.title || 'Approach step'}
                    className="phase-image-img"
                    style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      opacity: { duration: 1.5, ease: "easeInOut" }
                    }}
                  />
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </div>
    </motion.section>
  );
}

export default ApproachSection;
